import { useStatsigClient } from '@statsig/react-bindings';

/**
 * Hook to get the JotAudit introduction variant from a Statsig experiment.
 *
 * Tests two approaches to introducing users to JotAudit:
 * - Control ("Transparent"): Show EntitlementUsageCard upfront (transparent about limits)
 * - Treatment ("Discovery"): Show only diamond icon (discovery-based)
 *
 * This experiment measures whether hiding usage limits upfront leads to more
 * repeated engagement with JotAudit (measured by average sidebar opens per user).
 *
 * @returns Object with variant details and display flags
 *
 * @example
 * const { variant, showUsageCard, showDiamondIcon } = useJotAuditIntroExperiment();
 * // Control group: variant='transparent', showUsageCard=true, showDiamondIcon=false
 * // Treatment group: variant='discovery', showUsageCard=false, showDiamondIcon=true
 */
export function useJotAuditIntroExperiment() {
  const { client } = useStatsigClient();
  // 'jotaudit_intro_flow' matches the Statsig experiment ID
  const exp = client.getExperiment('jotaudit_intro_flow'); // exposure auto-logged

  const variant = exp.get('variant', 'transparent'); // default to control
  const showUsageCard = exp.get('show_usage_card', true); // default: show card (control)
  const showDiamondIcon = exp.get('show_diamond_icon', false); // default: hide icon (control)

  return {
    variant, // 'transparent' | 'discovery'
    showUsageCard, // boolean
    showDiamondIcon, // boolean
    isControl: variant === 'transparent',
    isTreatment: variant === 'discovery',
  };
}
